/**
 * platformUrl — frontend mirror of backend/src/utils/platformUrl.js.
 *
 * Single source of truth for building canonical profile URLs for any
 * MasterPlatform. Keeps the same shape and behavior as the backend helper
 * so URL construction is consistent across the admin console and the API.
 *
 * When in doubt, the backend helper is authoritative — this file is kept
 * in sync manually. If you change one, change the other.
 */

/**
 * Normalize a full URL:
 *   - drop "www." subdomain
 *   - drop trailing slash (except where the path is "/")
 *   - force https://
 *
 * Returns the input unchanged if it fails to parse.
 */
export function normalizeUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    u.hostname = u.hostname.replace(/^www\./i, '');
    u.protocol = 'https:';
    let s = u.toString();
    if (s.endsWith('/') && (u.pathname === '/' || u.pathname === '')) {
      s = s.slice(0, -1);
    }
    return s;
  } catch {
    return trimmed;
  }
}

/**
 * Resolve a YouTube-specific input into a canonical channel URL.
 *
 * Accepts:
 *   - bare channel ID "UCicM…"
 *   - "@handle" or "handle"
 *   - full URL in any of the supported shapes
 *   - legacy "/c/CustomName" or "/user/legacyusername"
 *
 * @param input
 * @param origin — base origin to use. Defaults to 'https://www.youtube.com'.
 *   Pass 'https://music.youtube.com' for YouTube Music URLs.
 * @returns Canonical URL string, or null if the input cannot be resolved.
 */
/**
 * Extract the canonical host from a URL string.
 * Returns null on parse failure. Mirrors backend/src/utils/platformUrl.js.
 */
export function hostFromUrl(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  try {
    return normalizeHost(new URL(url).hostname);
  } catch {
    return null;
  }
}

/**
 * Normalize a hostname: strip www., apply aliases, lowercase.
 */
function normalizeHost(hostname: string): string | null {
  if (!hostname) return null;
  let host = hostname.replace(/^www\./i, '').toLowerCase();
  // Keep in sync with backend HOST_ALIASES
  const aliases: Record<string, string> = {
    'twitter.com': 'x.com',
    'www.twitter.com': 'x.com',
    'music.amazon.com': 'music.amazon.in',
    'music.amazon.co.uk': 'music.amazon.in',
    'music.amazon.de': 'music.amazon.in',
    'music.amazon.fr': 'music.amazon.in',
    'music.amazon.it': 'music.amazon.in',
    'music.amazon.es': 'music.amazon.in',
    'music.amazon.co.jp': 'music.amazon.in',
    'music.amazon.ca': 'music.amazon.in',
    'music.amazon.com.au': 'music.amazon.in',
    'music.amazon.com.br': 'music.amazon.in',
    'music.amazon.com.mx': 'music.amazon.in',
  };
  host = aliases[host] || host;
  return host;
}

export function youtubeUrlFromInput(input: string, origin: string = 'https://www.youtube.com'): string | null {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Full URL — extract the meaningful path and re-canonicalize.
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      const host = u.hostname.replace(/^www\./i, '');
      if (!/(^|\.)youtube\.com$/i.test(host) && host !== 'youtu.be') {
        return null;
      }
      // Preserve music.youtube.com as the origin when the input is a
      // YouTube Music URL, so the canonical URL stays on the right domain.
      const origin = host === 'music.youtube.com'
        ? 'https://music.youtube.com'
        : 'https://www.youtube.com';
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs.length === 0) return null;

      // /channel/UC… → canonical channel URL
      if (segs[0] === 'channel' && segs[1]) {
        return `${origin}/channel/${segs[1]}`;
      }
      // Bare "youtube.com/UC…" (the broken form) → canonical channel URL.
      if (segs.length === 1 && /^UC[A-Za-z0-9_-]{22}$/.test(segs[0])) {
        return `${origin}/channel/${segs[0]}`;
      }
      // /@handle or /c/Name or /user/Name or /watch etc. — keep as-is
      return `${origin}/${segs.join('/')}`;
    } catch {
      return null;
    }
  }

  // Bare channel ID (24 chars, starts with UC) → canonical channel URL.
  if (/^UC[A-Za-z0-9_-]{22}$/.test(trimmed)) {
    return `${origin}/channel/${trimmed}`;
  }

  // @handle or bare handle → vanity URL.
  const cleaned = trimmed.replace(/^@+/, '');
  if (cleaned) {
    return `${origin}/@${cleaned}`;
  }

  return null;
}

/**
 * Build a URL for a platform whose artist URLs require a fixed path prefix
 * (e.g. Spotify /artist/, Amazon Music /artists/). The builder is idempotent:
 * it works whether the master baseURL already includes the prefix or not.
 */
function buildArtistUrl(baseURL: string, input: string, prefix: string): string | null {
  if (/^https?:\/\//i.test(input)) return normalizeUrl(input);
  const base = baseURL.replace(/\/$/, '');
  const handle = String(input).replace(/^\/+/, '');
  if (!handle) return null;

  const prefixPath = prefix.replace(/\/$/, '');
  const baseEndsWithPrefix = new RegExp(`/${escapeRegExp(prefixPath)}$`).test(base);

  // Handle already starts with the prefix → strip it to avoid duplication
  // (happens when baseURL ends with the prefix AND the handle was extracted
  // from a full URL that already contained the prefix).
  const cleanHandle = handle.startsWith(prefix) ? handle.slice(prefix.length) : handle;

  // baseURL already ends with /artist/ etc. → just append the clean handle
  if (baseEndsWithPrefix) {
    return `${base}/${cleanHandle}`;
  }

  // Otherwise prepend the prefix
  return `${base}/${prefix}${cleanHandle}`;
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Per-platform URL builder map. Each entry receives the master platform's
 * `baseURL` plus the raw user input and returns a canonical URL.
 *
 * The default strategy is `${baseURL}/${handle}` which is correct when the
 * handle has already been made relative to baseURL (see
 * extractHandleForDisplay). Platform-specific builders handle the fixed
 * /artist/ or /artists/ prefixes used by audio-streaming services.
 */
const PLATFORM_URL_BUILDERS: Record<string, (baseURL: string, input: string) => string | null> = {
  youtube: (_baseURL, input) => youtubeUrlFromInput(input),
  // YouTube Music uses the same URL shapes as YouTube (/channel/UC…, /@handle)
  // so the same resolver works for both, but with a different origin.
  'youtube music': (_baseURL, input) => youtubeUrlFromInput(input, 'https://music.youtube.com'),
  spotify: (baseURL, input) => buildArtistUrl(baseURL, input, 'artist/'),
  gaana: (baseURL, input) => buildArtistUrl(baseURL, input, 'artist/'),
  jiosaavn: (baseURL, input) => buildArtistUrl(baseURL, input, 'artist/'),
  'amazon music': (baseURL, input) => buildArtistUrl(baseURL, input, 'artists/'),
  // Wikipedia article URLs: normalize to the canonical en.wikipedia.org/wiki/ form.
  wikipedia: (baseURL, input) => {
    if (/^https?:\/\//i.test(input)) return normalizeUrl(input);
    let base = (baseURL || 'https://en.wikipedia.org/wiki').replace(/\/$/, '');
    let handle = String(input).replace(/^\/+/, '');
    if (!handle) return null;
    // Clean up duplicated wiki/ prefix from old/corrupted handles.
    while (handle.startsWith('wiki/')) {
      handle = handle.slice(5);
    }
    if (!base.endsWith('/wiki')) {
      base = `${base}/wiki`;
    }
    return `${base}/${handle}`;
  },
  'apple music': (baseURL, input) => {
    if (/^https?:\/\//i.test(input)) {
      // Apple Music URLs include a regional prefix (e.g. /in/artist/...) and
      // may point to the exact localized page. Keep the original URL intact.
      return input;
    }
    const base = baseURL.replace(/\/$/, '');
    const handle = String(input).replace(/^\/+/, '');
    if (!handle) return null;

    // baseURL already ends with /artist/ or /{locale}/artist/ → append directly
    if (/(?:\/[a-z]{2}(-[a-zA-Z]{2})?)?\/artist$/.test(base)) {
      return `${base}/${handle}`;
    }

    // Handle already includes the locale + /artist/ prefix → append as-is
    if (/^(?:[a-z]{2}(-[a-zA-Z]{2})?\/)?artist\//.test(handle)) {
      return `${base}/${handle}`;
    }

    // Default: add /artist/
    return `${base}/artist/${handle}`;
  },
};

/**
 * Return the path of `url` relative to `baseURL`.
 *
 * Example:
 *   baseURL = 'https://open.spotify.com/artist/'
 *   url     = 'https://open.spotify.com/artist/7HCqGPJcQTyGJ2yqntbuyr'
 *   returns '7HCqGPJcQTyGJ2yqntbuyr'
 *
 * Returns null when origins differ or parsing fails.
 */
function getRelativePath(baseURL: string, url: string): string | null {
  try {
    const base = new URL(baseURL);
    const u = new URL(url);
    const baseHost = base.hostname.replace(/^www\./i, '').toLowerCase();
    const uHost = u.hostname.replace(/^www\./i, '').toLowerCase();
    if (baseHost !== uHost) return null;

    const basePath = base.pathname.replace(/\/$/, '');
    const uPath = u.pathname;
    let relative = '';
    if (basePath && uPath.startsWith(basePath + '/')) {
      relative = uPath.slice(basePath.length + 1);
    } else if (basePath && uPath === basePath) {
      relative = '';
    } else {
      relative = uPath.replace(/^\//, '');
    }

    // If the baseURL itself ends with a path segment that is duplicated at the
    // start of the relative path, strip the duplicate. This cleans up URLs
    // produced by older code such as /wiki/wiki/Article or /artist/artist/id.
    const baseSegments = basePath.split('/').filter(Boolean);
    const lastBaseSegment = baseSegments[baseSegments.length - 1];
    if (lastBaseSegment && relative.startsWith(`${lastBaseSegment}/`)) {
      relative = relative.slice(lastBaseSegment.length + 1);
    }

    return relative;
  } catch {
    return null;
  }
}

/**
 * Extract the display handle from a platform URL.
 *
 * The returned value is relative to the master platform's baseURL, so
 * `buildPlatformUrl(master, handle)` reconstructs the original URL. This works
 * whether the baseURL is just the origin (e.g. https://open.spotify.com) or
 * already includes a path prefix (e.g. https://open.spotify.com/artist/).
 *
 * YouTube and YouTube Music keep their special channel/@handle handling.
 */
export function extractHandleForDisplay(
  masterPlatform: { title?: string; baseURL?: string } | null | undefined,
  url: string | null | undefined,
): string {
  if (!masterPlatform || !url) return '';
  const title = (masterPlatform.title || '').toLowerCase();
  const trimmed = String(url).trim();
  if (!trimmed) return '';

  // YouTube / YouTube Music: use the canonical resolver, then return the
  // meaningful segment (channel ID or @handle).
  if (title === 'youtube' || title === 'youtube music') {
    const origin = title === 'youtube music' ? 'https://music.youtube.com' : 'https://www.youtube.com';
    const canonical = youtubeUrlFromInput(trimmed, origin);
    if (!canonical) return trimmed;
    try {
      const u = new URL(canonical);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'channel' && segs[1]) return segs[1];
      if (segs.length > 0) return segs[segs.length - 1];
      return trimmed;
    } catch {
      return trimmed;
    }
  }

  // Wikipedia: return the article name, ignoring which subdomain/host serves it.
  if (title === 'wikipedia') {
    try {
      const u = new URL(trimmed);
      const segs = u.pathname.split('/').filter(Boolean);
      const idx = segs.indexOf('wiki');
      if (idx !== -1 && segs[idx + 1]) {
        let article = segs.slice(idx + 1).join('/');
        // Clean up duplicated /wiki/wiki/Article paths from old data.
        while (article.startsWith('wiki/')) {
          article = article.slice(5);
        }
        return article;
      }
      return segs[segs.length - 1] || '';
    } catch {
      return trimmed;
    }
  }

  // Full URL: return the path relative to baseURL.
  if (/^https?:\/\//i.test(trimmed)) {
    const relative = getRelativePath(masterPlatform.baseURL || '', trimmed);
    if (relative != null) return relative;
    // Fallback if origins differ: return the full path.
    try {
      return new URL(trimmed).pathname.replace(/^\//, '');
    } catch {
      return trimmed;
    }
  }

  // Not a URL — return as-is.
  return trimmed;
}

/**
 * Build a canonical profile URL for a master platform.
 *
 * @param masterPlatform — MasterPlatforms record (must have `title` and `baseURL`).
 * @param input — handle, partial path, or full URL.
 * @returns Canonical URL, or null if input is empty/invalid.
 */
export function buildPlatformUrl(
  masterPlatform: { title?: string; baseURL?: string } | null | undefined,
  input: string | null | undefined,
): string | null {
  if (!masterPlatform || !input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;

  const title = (masterPlatform.title || '').toLowerCase();
  const builder = PLATFORM_URL_BUILDERS[title];
  if (builder) {
    const url = builder(masterPlatform.baseURL || '', trimmed);
    return url ? normalizeUrl(url) : null;
  }

  // Default: baseURL + handle, with a single slash.
  if (/^https?:\/\//i.test(trimmed)) {
    return normalizeUrl(trimmed);
  }
  const base = (masterPlatform.baseURL || '').replace(/\/$/, '');
  const handle = trimmed.replace(/^\/+/, '');
  if (!base || !handle) return null;
  return `${base}/${handle}`;
}

/**
 * Build a "Visit profile" URL for a PlatformEntry, preferring the canonical
 * `url` already stored on the record (which is correct after prefill) and
 * falling back to `baseURL + handle` only when no URL is available.
 *
 * Returns an empty string if no URL can be constructed.
 */
export function buildVisitUrl(entry: {
  url?: string;
  baseURL?: string;
  handle?: string;
  platformName?: string;
}): string {
  // If the entry has a stored URL, trust it — prefill wrote the canonical
  // URL there, so any further reconstruction in the UI would be redundant
  // (and wrong for YouTube, where baseURL + handle produces a 404).
  if (entry.url && /^https?:\/\//i.test(entry.url)) {
    return normalizeUrl(entry.url);
  }
  // Fallback: build from baseURL + handle using the platform-specific helper.
  const fromHandle = buildPlatformUrl(
    { title: entry.platformName, baseURL: entry.baseURL },
    entry.handle,
  );
  return fromHandle || '';
}

export default {
  buildPlatformUrl,
  normalizeUrl,
  youtubeUrlFromInput,
  buildVisitUrl,
  hostFromUrl,
  extractHandleForDisplay,
};
